/**
 * TreatmentFit.tsx — "why this treatment suits you", under the plan choices.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY (2026-08-17, business): the builder asks a patient to commit to months of
 * a prescription medicine having told them only what it costs. This band answers
 * the question they are actually holding — why this one, and what will it
 * actually do — at the point of the decision rather than in a support email
 * afterwards.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS SAFE TO SAY HERE, AND WHAT IS NOT.
 *
 * The mechanism lines below (appetite, digestion, cravings) are DRUG-CLASS
 * copy, chosen by the route of administration the server sent — not a claim
 * about this patient, not a number, and not an outcome. That is why they can be
 * static: "this class of medicine works by slowing digestion" is a statement
 * about the medicine, and it does not change because of who is reading it.
 *
 * NOTHING PATIENT-SPECIFIC IS DERIVED HERE. The BMI line renders only when `bmi`
 * is supplied by the caller, and no threshold arithmetic happens in this file —
 * if the portal decided "you qualify because…" we would have two places deciding
 * who qualifies, and they drift apart the first time a rule changes. The
 * sentence reflects the clinic's eligibility decision; it never makes one.
 *
 * NO OUTCOME CLAIMS. Nothing here says how much weight anyone will lose; that
 * belongs to `WeightProjection`, which is server-fed for exactly that reason.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * R28: no brand, no delivery-form word, no strength. The route is classified
 * from the server's `form` string by the same helper the treatment cards use.
 *
 * Accessibility: an ordinary heading + list. The tick marks are `aria-hidden`
 * decoration — each row's meaning is in its text, never in the glyph or colour.
 */
import { shapeForForm } from './TreatmentFormIcon';

interface Mechanism {
  title: string;
  body: string;
}

/**
 * How this class of medicine works, by route.
 *
 * Deliberately about the MEDICINE, not the person: safe to hold in source
 * precisely because it makes no personal claim. If these ever need to differ per
 * product, they move to the catalogue — do not start branching on a name here,
 * which is both an R28 violation and a second source of clinical truth.
 */
const MECHANISMS: Record<'injected' | 'swallowed', Mechanism[]> = {
  injected: [
    { title: 'Appetite', body: 'Reduces appetite, so you feel satisfied with smaller portions.' },
    { title: 'Digestion', body: 'Slows how quickly your stomach empties, so you stay fuller for longer.' },
    { title: 'Cravings', body: 'Quietens the constant thinking about food, so cravings are easier to manage.' },
  ],
  swallowed: [
    { title: 'Appetite', body: 'Helps you feel satisfied sooner, so portions come down without willpower alone.' },
    // Says what this route IS, not what it is not: naming the alternative route
    // would put a banned delivery-form word in source (R28 scans this tree).
    { title: 'Routine', body: 'Taken daily at home, with nothing to keep refrigerated.' },
    { title: 'Cravings', body: 'Makes the urge to snack between meals easier to ride out.' },
  ],
};

export function TreatmentFit({
  form,
  bmi,
}: {
  /** The server's formulation string for the SELECTED treatment. */
  form: string | null | undefined;
  /**
   * The patient's BMI, supplied by the caller. Absent ⇒ the eligibility sentence
   * is omitted entirely and the generic line stands in.
   *
   * NEVER derive it in this file. `JourneyPage` passes the value from `bmiOf` —
   * the portal's ONE derivation, and the same figure the ResultScreen's dial
   * showed the patient one screen earlier. A second computation here would not
   * be "safer": it would be a second answer, and the visible failure is two
   * different BMIs on consecutive screens. Eligibility itself remains the
   * clinic's decision — this sentence reflects it, it does not make it.
   */
  bmi?: number | null;
}) {
  const shape = shapeForForm(form);
  // An unrecognised route means we cannot say how it works without guessing, and
  // a guess about a prescription medicine is worse than saying nothing.
  if (!shape) return null;

  const mechanisms = MECHANISMS[shape];

  return (
    <section
      // `gps-jux-panel`: the journey's one white card (tokens.css, P7).
      className="gps-jux-panel"
      style={{
        background: 'var(--gps-surface)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        padding: 'var(--gps-s-4)',
        marginTop: 'var(--gps-s-5)',
      }}
    >
      <h2 style={{ fontSize: '18px', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
        Why this treatment suits you
      </h2>

      {typeof bmi === 'number' ? (
        <p style={{ margin: '0 0 14px', fontSize: '14px', color: 'var(--gps-muted)', lineHeight: 1.55 }}>
          Your BMI of <strong style={{ color: 'var(--gps-text)' }}>{bmi.toFixed(1)}</strong>, together with the rest of
          your answers, is why a clinician can consider this treatment for you. They review everything before anything
          is approved.
        </p>
      ) : (
        <p style={{ margin: '0 0 14px', fontSize: '14px', color: 'var(--gps-muted)', lineHeight: 1.55 }}>
          Based on your answers, a clinician can consider this treatment for you. They review everything before
          anything is approved.
        </p>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {mechanisms.map((m, i) => (
          <li
            key={m.title}
            style={{
              display: 'flex',
              gap: '11px',
              alignItems: 'flex-start',
              padding: '12px 0',
              borderTop: i === 0 ? 'none' : '1px solid var(--gps-border)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '22px',
                height: '22px',
                flex: 'none',
                borderRadius: 'var(--gps-r-pill)',
                background: 'var(--gps-primary-light)',
                color: 'var(--gps-green-text)',
                display: 'grid',
                placeItems: 'center',
                fontSize: '12px',
                fontWeight: 800,
              }}
            >
              ✓
            </span>
            <span>
              <span style={{ display: 'block', fontWeight: 650, fontSize: '14px' }}>{m.title}</span>
              <span style={{ display: 'block', fontSize: '13.5px', color: 'var(--gps-muted)', marginTop: '2px' }}>
                {m.body}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/* Says what it is: a description of the medicine, not a forecast for this
          person. Without it the list reads as a promise of results. */}
      <p style={{ margin: '12px 0 0', fontSize: '12.5px', color: 'var(--gps-muted)', lineHeight: 1.5 }}>
        How this type of medication works. It is not a prediction of your own results, and it is not a substitute for
        the advice your clinician gives you.
      </p>
    </section>
  );
}
